import { useCallback, useState } from 'react';
import { UserImageStore } from '../../../UserData/_stores/UserImageStore';
import { useDispatch, useSelector } from 'react-redux';
import WebsiteStore from '../../../WebsiteStore';
import useLabData from '../../_hooks/useLabData';
import { AddImgLayer } from '../../../UIData/_actions/DesignLabStoreActions';
import useCalcFitTemplate from '../../_hooks/useCalcFitTemplate';
import useCalcFitToSide from '../../_hooks/useCalcFitToSide';
import { Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '../../../Common/_hooks/useAppDispatch';
import IconButton from '../../../Common/Buttons/IconButton';
import ImageLibraryBrowser from '../../ImageLibraryBrowser/_components/ImageLibraryBrowser';
import { useAppSelector } from '../../../Common/_hooks/useAppSelector';
import useSceneData from '../../_hooks/useSceneData';
import { getImageSizeAtDpi } from '../../../Common/Measurements/imageResolution';
import { clampInitialImageScale, MIN_LAYER_SIZE } from '../../imageSizing';

export default function AddImageTool() {
  const dispatch = useAppDispatch();
  const [t] = useTranslation('design-lab');
  const activeScene = useAppSelector(state => state.get('UIData').get('designLab').get('activeScene'));
  const activeSubproduct = useAppSelector(state => state.get('UIData').get('designLab').get('activeSubproduct'));
  const autoDesign = useAppSelector(state => activeSubproduct !== null ? state.get('UIData').get('designLab').get('autoDesignSubproducts').get(activeSubproduct) : undefined);
  const labData = useLabData();
  const sceneData = useSceneData();
  const calcFitTemplate = useCalcFitTemplate();
  const calcFitToSide = useCalcFitToSide();

  const [libraryOpened,setLibraryOpened] = useState(false)

  const onSelectImage = useCallback((item: UserImageStore) => {
    if(!activeScene || !labData) return

    const maxWidth = 1024;
    const maxHeight = 1024;

    let originalWidth = item.get('width');
    let originalHeight = item.get('height');
    let width, height;
    if (originalWidth / originalHeight > 1) {
      width = maxWidth;
      height = maxHeight * originalHeight / originalWidth;
    } else {
      height = maxHeight;
      width = maxWidth * originalWidth / originalHeight;
    }

    const dpiSize = getImageSizeAtDpi(
      item.get('width'), item.get('height'), item.get('dpi'),
      sceneData ? sceneData.ratio * labData.dpi / 300 : 0,
    );
    if (dpiSize) {
      width = dpiSize.width;
      height = dpiSize.height;
    }
    const data = labData.fit_all ? calcFitTemplate(width, height) : calcFitToSide(width, height, 'left');
    const scale = dpiSize
      ? clampInitialImageScale(1, MIN_LAYER_SIZE / Math.min(width, height), data.scaleX)
      : data.scaleX;

    dispatch(AddImgLayer({
      fileid: item.get('fileid'),
      src: '/api/user/images/image/' + item.get('fileid'),
      originalWidth: originalWidth,
      originalHeight: originalHeight,
      scene: activeScene,
      subproduct: activeSubproduct,
      width: width,
      height: height,
      ...data,
      scaleX: scale,
      scaleY: scale,
    }))
  }, [labData, sceneData, activeScene, activeSubproduct, calcFitTemplate, calcFitToSide]);

  const onLibraryOpen = useCallback(() => {
    setLibraryOpened(true)
  }, [])

  const onLibraryClose = useCallback(() => {
    setLibraryOpened(false)
  }, [])

  return <>
    <Tooltip title={t('Add Image')}>
      <IconButton
        variant="contained"
        color="primary"
        size="large"
        disabled={!!autoDesign}
        onClick={onLibraryOpen}
      ><img src={require('@resources/img/create/design-lab-v4/icons/add-image.svg?url')} /></IconButton>
    </Tooltip>

    <ImageLibraryBrowser
      opened={libraryOpened}
      onSelect={onSelectImage}
      onClose={onLibraryClose}
    />
  </>
}
